import { useMemo, type ReactNode } from "react";
import { Router, type BaseLocationHook } from "wouter";
import { navigate as browserNavigate, usePathname } from "wouter/use-browser-location";
import { INSTANCE_PATH_PREFIX } from "@shared/instanceAddress";
import { instancePath } from "@/lib/instancePath";
import { isGlobalPath } from "@/lib/routes";

/**
 * The router the shell runs in (round 9 T-58): its screens keep their relative paths and the
 * address carries the instance.
 *
 * Inside, `useLocation` reads the address without its `/i/<id>` prefix, and every `Link`, `navigate`
 * and `Redirect` adds it back, so `/connections`, a nested section's `navigate("/new")` and an
 * escape to the root (`absolutePath`, wouter's `~`) all stay in this instance. A screen outside
 * every instance (`isGlobalPath`: the picker, a platform or personal screen) keeps its own
 * address. A platform screen rendered with an instance selected uses the same router, so its nav
 * links go straight into that instance.
 */
function instanceLocationHook(instanceId: string | null, segment?: string): BaseLocationHook {
  // The address's own segment when it has one: an id that is not a uuid is never re-encoded, so a
  // link clicked on the "no access" page does not grow `%25`s.
  const prefix =
    instanceId === null
      ? null
      : segment !== undefined
        ? `${INSTANCE_PATH_PREFIX}/${segment}`
        : instancePath(instanceId, "/");
  const strip = (path: string): string => {
    if (prefix === null) return path;
    if (path === prefix) return "/";
    return path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path;
  };
  const toAddress = (to: string): string => {
    // An address already in an instance (a switch, a link a screen was given) stays as written,
    // and with no instance selected every address does; so does a search or a hash on its own,
    // which belongs to the screen on show.
    if (prefix === null || to.startsWith(`${INSTANCE_PATH_PREFIX}/`) || /^[?#]/.test(to)) {
      return to;
    }
    const pathname = to.split(/[?#]/)[0] ?? to;
    if (isGlobalPath(pathname)) return to;
    return to === "/" ? prefix : `${prefix}${to.startsWith("/") ? to : `/${to}`}`;
  };
  const hook = ((options?: { ssrPath?: string }) => {
    const pathname = usePathname(options);
    return [
      strip(pathname),
      (to: string, navOptions?: { replace?: boolean; state?: unknown }) =>
        browserNavigate(toAddress(to), navOptions),
    ];
  }) as BaseLocationHook & { hrefs?: (href: string) => string };
  hook.hrefs = toAddress;
  return hook;
}

export function InstanceRouter({
  instanceId,
  segment,
  children,
}: {
  /** Null: no instance selected, so addresses pass through as written. */
  instanceId: string | null;
  /** The address's own segment, when the shell is mounted from an address under `/i/`. */
  segment?: string;
  children: ReactNode;
}) {
  const hook = useMemo(() => instanceLocationHook(instanceId, segment), [instanceId, segment]);
  return (
    <Router hook={hook} hrefs={hook.hrefs as (href: string) => string}>
      {children}
    </Router>
  );
}
